import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { refreshAccessClaims } from '../lib/jwt-claims';

/**
 * Stripe Checkout success_url lands here. We refresh the session so the
 * has_active_access JWT claim picks up the new subscription state, then
 * forward the user into the feed.
 *
 * Without this refresh the user would still see the paywall (RequireAccess
 * has a refresh-on-stale fallback, but bouncing through here is cleaner
 * UX than appearing-to-fail-then-self-recover).
 */

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // The Stripe webhook that writes the subscription row races against this
    // redirect. A single refresh often fires before the row lands, bouncing a
    // paying user back to the paywall. Poll with backoff (~15s total) before
    // giving up, so the common case (webhook lands in a few seconds) just works.
    const delaysMs = [800, 1200, 1800, 2500, 3500, 5000];
    (async () => {
      for (let attempt = 0; attempt <= delaysMs.length; attempt++) {
        try {
          const claims = await refreshAccessClaims();
          if (cancelled) return;
          if (claims?.hasActiveAccess) {
            navigate('/', { replace: true });
            return;
          }
        } catch (err: any) {
          if (cancelled) return;
          // Transient refresh failure — keep polling unless it's the last try.
          if (attempt === delaysMs.length) {
            setError(err?.message ?? "Couldn't refresh your session. Try refreshing the page.");
            return;
          }
        }
        if (attempt < delaysMs.length) {
          await new Promise((r) => setTimeout(r, delaysMs[attempt]));
        }
      }
      if (cancelled) return;
      // Webhook still hasn't written the row after the full window.
      setError("Your payment went through. Stripe is finalising the subscription. This usually takes a few seconds.");
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <div style={{
      minHeight: '60dvh',
      display: 'grid',
      placeItems: 'center',
      padding: 'var(--space-12) var(--gutter)',
      textAlign: 'center',
    }}>
      <div style={{ maxWidth: 480 }}>
        <p className="kicker">Conversion Club</p>
        <h1 style={{ fontFamily: 'var(--f-display)', fontSize: 'var(--t-2xl)', margin: 'var(--space-4) 0' }}>
          {error ? 'Almost there.' : "You're in. Loading the room…"}
        </h1>
        {error && (
          <>
            <p style={{ fontFamily: 'var(--f-body)', color: 'var(--text-soft)' }}>{error}</p>
            <button
              type="button"
              className="btn btn--primary btn--md"
              style={{ marginTop: 'var(--space-4)' }}
              onClick={() => window.location.reload()}
            >
              Refresh now
            </button>
          </>
        )}
      </div>
    </div>
  );
}
